<template>
  <v-app>
    <v-main>
      <v-container fluid class="cand-detail">
        <!-- Header curat de workspace -->
        <div class="cand-detail__header">
          <div class="cand-detail__header-left">
            <v-btn variant="text" size="small" class="cand-detail__back" @click="$router.back()">
              <v-icon start>mdi-arrow-left</v-icon>{{ $t("candidateDetails.toolbar.back") }}
            </v-btn>
            <v-icon class="cand-detail__header-icon" size="18">mdi-account-outline</v-icon>
            <div class="cand-detail__titles">
              <h1 class="cand-detail__title">{{ candidate.FirstName }} {{ candidate.LastName }}</h1>
              <span v-if="candidate.Profession" class="cand-detail__subtitle">{{ candidate.Profession }}</span>
            </div>
          </div>
          <v-btn color="primary" variant="flat" prepend-icon="mdi-pencil" @click="openEditDialog">
            {{ $t("app.actions.edit") }}
          </v-btn>
        </div>

        <!-- Panouri detaliu candidat (dense, conform docs/design.md L76-L78, L85) -->
        <v-row class="cd-panels my-2">
          <!-- Proprietăți candidat (dense property panel) -->
          <v-col cols="12" md="4">
            <v-card variant="flat" class="app-content-card cd-panel">
              <div class="cd-panel__head">
                <v-icon size="15" class="cd-panel__head-icon">mdi-account-outline</v-icon>
                <span>{{ $t("candidateDetails.sections.candidate") }}</span>
              </div>
              <div class="cd-props">
                <div v-for="row in propertyRows" :key="row.label" class="cd-prop">
                  <span class="cd-prop__label">{{ row.label }}</span>
                  <span class="cd-prop__value">{{ row.value || "—" }}</span>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Experiență profesională + linkuri + aplicații -->
          <v-col cols="12" md="4">
            <v-card variant="flat" class="app-content-card cd-panel">
              <div class="cd-panel__head">
                <v-icon size="15" class="cd-panel__head-icon">mdi-briefcase-outline</v-icon>
                <span>{{ $t("candidateDetails.sections.jobs") }}</span>
              </div>
              <div v-if="jobs.length" class="cd-jobs">
                <div v-for="job in jobs" :key="job.id" class="cd-job">
                  <div class="cd-job__title">{{ job.Job_title }}</div>
                  <div class="cd-job__meta">{{ job.Company }}</div>
                  <div class="cd-job__meta">
                    {{ job.Date_finish ? formatDate(job.Date_finish) : $t("candidateDetails.jobs.inProgress") }}
                  </div>
                </div>
              </div>
              <div v-if="linkChips.length" class="cd-links">
                <a
                  v-for="lnk in linkChips"
                  :key="lnk.label + lnk.url"
                  :href="lnk.url"
                  target="_blank"
                  rel="noopener"
                  class="cd-link-chip"
                >
                  <v-icon size="14">{{ lnk.icon }}</v-icon> {{ lnk.label }}
                </a>
              </div>
              <div class="cd-applications">
                <CD_Recrutare :candidateId="id" @cv-sent="fetchComments" />
              </div>
            </v-card>
          </v-col>

          <!-- Skills (chips) -->
          <v-col cols="12" md="4">
            <v-card variant="flat" class="app-content-card cd-panel">
              <div class="cd-panel__head">
                <v-icon size="15" class="cd-panel__head-icon">mdi-star-outline</v-icon>
                <span>{{ $t("candidateDetails.sections.skills") }}</span>
              </div>
              <div v-if="skills.length" class="cd-skills">
                <v-chip v-for="skill in skills" :key="skill.id">
                  {{ skill.Skill }}
                </v-chip>
              </div>
              <div v-else class="cd-empty">—</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabs -->
        <v-card>
          <div class="d-flex flex-row">
            <v-tabs v-model="tab" color="primary" direction="vertical">
              <v-tab
                v-for="section in i18nSections"
                :key="section.value"
                :value="section.value"
              >
                <v-icon left>{{ section.icon }}</v-icon>
                {{ section.text }}
              </v-tab>
            </v-tabs>
            <v-tabs-window v-model="tab" class="flex-grow-1">
              <!-- Comentarii -->
              <v-tabs-window-item value="comments">
                <CandidateCommentsTab
                  :candidate-id="id"
                  :items="comments"
                  :current-user="currentUser"
                  @refresh="fetchComments"
                  @notify="notify"
                />
              </v-tabs-window-item>

              <!-- Despre candidat -->
              <v-tabs-window-item value="thecandidate">
                <v-card flat>
                  <v-card-text>
                    <!-- Doar câmpul unic „Despre candidat"; restul proprietăților sunt în cardul de sus. -->
                    <v-card variant="flat" class="my-2 app-content-card">
                      <v-card-text class="text-left">
                        <v-card-title class="px-0">{{ candidate.FirstName }} {{ candidate.LastName }}</v-card-title>
                        <v-card-subtitle class="px-0">{{ candidate.Profession }}</v-card-subtitle>
                        <v-divider class="my-3"></v-divider>
                        <p class="mb-1"><strong>{{ $t("candidateDetails.card.about") }}</strong></p>
                        <p class="cand-detail__about">
                          {{ candidate.AboutCandidate || $t("candidateDetails.card.noAbout") }}
                        </p>
                      </v-card-text>
                    </v-card>
                  </v-card-text>
                </v-card>
              </v-tabs-window-item>

              <!-- Limbi -->
              <v-tabs-window-item value="languages">
                <CandidateLanguagesTab
                  :candidate-id="id"
                  :items="languages"
                  @refresh="fetchCandidateDetails"
                  @notify="notify"
                />
              </v-tabs-window-item>

              <!-- Abilități -->
              <v-tabs-window-item value="skills">
                <CandidateSkillsTab
                  :candidate-id="id"
                  :items="skills"
                  @refresh="fetchCandidateDetails"
                  @notify="notify"
                />
              </v-tabs-window-item>

              <!-- Certificări -->
              <v-tabs-window-item value="certifications">
                <CandidateCertificationsTab
                  :candidate-id="id"
                  :items="certifications"
                  @refresh="fetchCandidateDetails"
                  @notify="notify"
                />
              </v-tabs-window-item>

              <!-- Educație -->
              <v-tabs-window-item value="education">
                <CandidateEducationTab
                  :candidate-id="id"
                  :items="education"
                  @refresh="fetchCandidateDetails"
                  @notify="notify"
                />
              </v-tabs-window-item>

              <!-- Experiență Profesională -->
              <v-tabs-window-item value="jobs">
                <CandidateExperienceTab
                  :candidate-id="id"
                  :items="jobs"
                  @refresh="fetchCandidateDetails"
                  @notify="notify"
                />
              </v-tabs-window-item>

              <!-- Recrutare -->
              <v-tabs-window-item value="recrutare">
                <v-card flat>
                  <v-card-text>
                    <CD_Recrutare :candidateId="id" @cv-sent="fetchComments" />
                  </v-card-text>
                </v-card>
              </v-tabs-window-item>

              <!-- Soft Skills -->
              <v-tabs-window-item value="softSkills">
                <CandidateSoftSkillsTab
                  :candidate-id="id"
                  :items="softSkills"
                  @refresh="fetchCandidateDetails"
                  @notify="notify"
                />
              </v-tabs-window-item>

              <!-- Link-uri -->
              <v-tabs-window-item value="links">
                <CandidateLinksTab
                  :candidate-id="id"
                  :items="candidateLinksArray"
                  @refresh="fetchCandidateDetails"
                  @notify="notify"
                />
              </v-tabs-window-item>

              <!-- Alte Documente -->
              <v-tabs-window-item value="otherDocuments">
                <CandidateDocumentsTab
                  :candidate-id="id"
                  :items="otherDocuments"
                  @refresh="fetchCandidateDetails"
                  @notify="notify"
                />
              </v-tabs-window-item>
            </v-tabs-window>
          </div>
        </v-card>
      </v-container>

      <!-- Dialog pentru Editarea Candidatului -->
      <CandidateFormDialog
        v-model="editDialog"
        :is-edit="isEdit"
        :candidate="editCandidate"
        @save="onDialogSave"
      />

      <!-- Dialoguri pentru Adăugare/Editare Tabele -->

      <!-- Snackbar pentru notificări -->
      <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        timeout="3000"
        top
        right
      >
        {{ snackbarMessage }}
        <v-btn icon="mdi-close" variant="text" size="small" :aria-label="$t('app.actions.close')" :title="$t('app.actions.close')" @click="snackbar = false" />
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import CD_Recrutare from "./candidate/CD_recrutare.vue";
import CandidateLanguagesTab from "./candidate/CandidateLanguagesTab.vue";
import CandidateSkillsTab from "./candidate/CandidateSkillsTab.vue";
import CandidateCertificationsTab from "./candidate/CandidateCertificationsTab.vue";
import CandidateEducationTab from "./candidate/CandidateEducationTab.vue";
import CandidateSoftSkillsTab from "./candidate/CandidateSoftSkillsTab.vue";
import CandidateLinksTab from "./candidate/CandidateLinksTab.vue";
import CandidateExperienceTab from "./candidate/CandidateExperienceTab.vue";
import CandidateCommentsTab from "./candidate/CandidateCommentsTab.vue";
import CandidateDocumentsTab from "./candidate/CandidateDocumentsTab.vue";
import CandidateFormDialog from "./candidate/CandidateFormDialog.vue";
import { createEmptyCandidate } from "@/Utile/candidateForm.mjs";

export default defineComponent({
  name: "CandidateDetails",
  components: {
    CD_Recrutare,
    CandidateLanguagesTab,
    CandidateSkillsTab,
    CandidateCertificationsTab,
    CandidateEducationTab,
    CandidateSoftSkillsTab,
    CandidateLinksTab,
    CandidateExperienceTab,
    CandidateCommentsTab,
    CandidateDocumentsTab,
    CandidateFormDialog,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      candidate: {},
      candidateLinks: {},
      comments: [],
      languages: [],
      skills: [],
      certifications: [],
      education: [],
      jobs: [],
      softSkills: [],
      otherDocuments: [],
      tab: "comments", // Tab implicit selectat
      // Proprietăți pentru dialogul de editare a candidatului
      editDialog: false,
      isEdit: true, // Vom fi întotdeauna în modul editare
      editCandidate: createEmptyCandidate(),
      rules: {
        required: (value) => !!value || "Acest câmp este obligatoriu",
      },
      // Proprietăți pentru Snackbar
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      currentUser: {},
    };
  },
  computed: {
    i18nSections() {
      const t = (key) => this.$t(`candidateDetails.sections.${key}`);
      return [
        { value: "comments", text: t("comments"), icon: "mdi-comment" },
        { value: "thecandidate", text: t("candidate"), icon: "mdi-account" },
        { value: "languages", text: t("languages"), icon: "mdi-language-c" },
        { value: "skills", text: t("skills"), icon: "mdi-clipboard-check" },
        { value: "certifications", text: t("certifications"), icon: "mdi-certificate" },
        { value: "education", text: t("education"), icon: "mdi-school" },
        { value: "jobs", text: t("jobs"), icon: "mdi-briefcase" },
        { value: "recrutare", text: t("recruitment"), icon: "mdi-briefcase-plus" },
        { value: "softSkills", text: t("softSkills"), icon: "mdi-human-handsup" },
        { value: "links", text: t("links"), icon: "mdi-link" },
        { value: "otherDocuments", text: t("documents"), icon: "mdi-file-document" },
      ];
    },
    // Transformă obiectul candidateLinks într-un array pentru a itera ușor
    candidateLinksArray() {
      if (
        !this.candidateLinks ||
        Object.keys(this.candidateLinks).length === 0
      ) {
        return [];
      }
      return [this.candidateLinks];
    },
    // Rânduri pentru property panel-ul dens (numele + profesia sunt deja în header).
    propertyRows() {
      const c = this.candidate || {};
      const yn = (v) => (v ? this.$t("candidates.values.yes") : this.$t("candidates.values.no"));
      const t = (k) => this.$t(`candidateDetails.card.${k}`);
      return [
        { label: t("id"), value: c.id },
        { label: t("phone"), value: c.PhoneNumber },
        { label: t("email"), value: c.Email },
        { label: t("country"), value: c.Country },
        { label: t("address"), value: c.Address },
        { label: t("marriageStatus"), value: c.MarriageStatus },
        { label: t("children"), value: c.Children },
        { label: t("dob"), value: this.formatDate(c.Dateofbirth) },
        { label: t("nationality"), value: c.Nationality },
        { label: t("lookingPosition"), value: c.LookingPosition },
        { label: t("specialization"), value: c.Specialization },
        { label: t("contacted"), value: yn(c.CandidateContacted) },
        { label: t("interested"), value: yn(c.InterestedInOffers) },
        { label: t("cvSource"), value: c.CVSource },
        { label: t("hasCv"), value: yn(c.HasCV) },
        { label: t("created"), value: this.formatDateTime(c.created_at) },
        { label: t("updated"), value: this.formatDateTime(c.updated_at) },
      ];
    },
    // Linkuri sociale — doar platformele completate, ca chip-uri (fără rânduri goale).
    linkChips() {
      const platforms = [
        { key: "LinkedIn", label: "LinkedIn", icon: "mdi-linkedin" },
        { key: "GitHub", label: "GitHub", icon: "mdi-github" },
        { key: "Facebook", label: "Facebook", icon: "mdi-facebook" },
        { key: "Instagram", label: "Instagram", icon: "mdi-instagram" },
      ];
      const out = [];
      for (const rec of this.candidateLinksArray) {
        for (const p of platforms) {
          const url = rec && rec[p.key];
          if (url) out.push({ label: p.label, url, icon: p.icon });
        }
      }
      return out;
    },
  },
  created() {
    this.fetchCandidateDetails();
    this.getCurrentUser();
    this.fetchComments();
  },
  methods: {
    formatDateTime(dateString) {
      if (!dateString) return "";
      const s = String(dateString).replace("T", " ");
      const [d, t] = s.split(" ");
      const m = (d || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      const day = m ? `${m[3]}.${m[2]}.${m[1]}` : d;
      const time = (t || "").slice(0, 5); // HH:mm
      return time ? `${day}, ${time}` : day;
    },
    // Preia utilizatorul curent pentru a popula Added_by la comentarii
    getCurrentUser() {
      axios.get('/me')
        .then((response) => {
          if (response.data) this.currentUser = response.data;
        })
        .catch((error) => {
          console.error('Eroare la obținerea utilizatorului:', error);
        });
    },

    // Preia comentariile pentru candidatul curent
    fetchComments() {
      axios
        .get('/candidati/comments', { params: { id_foreign: this.id } })
        .then((response) => {
          this.comments = Array.isArray(response.data)
            ? response.data.sort((a, b) => b.id - a.id)
            : [];
        })
        .catch((error) => {
          console.error('Eroare la obținerea comentariilor:', error);
        });
    },
    fetchCandidateDetails() {
      const candidateId = this.id;

      // Obține detaliile candidatului
      axios
        .get(`/candidati/candidats/${candidateId}`)
        .then((response) => {
          this.candidate = response.data;
        })
        .catch((error) => {
          console.error("Eroare la obținerea detaliilor candidatului:", error);
        });

      // Obține comentariile
      axios
        .get("/candidati/comments", { params: { id_foreign: candidateId } })
        .then((response) => {
          this.comments = response.data
            .sort((a, b) => b.id - a.id)
            .map((item) => ({
              ...item,
              Date: this.formatDate(item.Date),
            }));
        })
        .catch((error) => {
          console.error("Eroare la obținerea comentariilor:", error);
        });

      // Obține limbile
      axios
        .get("/candidati/languages", { params: { id_foreign: candidateId } })
        .then((response) => {
          this.languages = response.data
            .sort((a, b) => b.id - a.id)
            .map((item) => ({
              ...item,
              Date: this.formatDate(item.Date),
            }));
        })
        .catch((error) => {
          console.error("Eroare la obținerea limbilor:", error);
        });

      // Obține abilitățile
      axios
        .get("/candidati/skills", { params: { id_foreign: candidateId } })
        .then((response) => {
          this.skills = response.data;
        })
        .catch((error) => {
          console.error("Eroare la obținerea abilităților:", error);
        });

      // Obține certificările
      axios
        .get("/candidati/certification", {
          params: { id_foreign: candidateId },
        })
        .then((response) => {
          this.certifications = response.data;
        })
        .catch((error) => {
          console.error("Eroare la obținerea certificărilor:", error);
        });

      // Obține educația
      axios
        .get("/candidati/education", { params: { id_foreign: candidateId } })
        .then((response) => {
          this.education = response.data;
        })
        .catch((error) => {
          console.error("Eroare la obținerea educației:", error);
        });

      // Obține experiența profesională
      axios
        .get("/candidati/jobs", { params: { id_foreign: candidateId } })
        .then((response) => {
          this.jobs = response.data
            .sort((a, b) => {
              // Dacă a.Date_finish este null sau undefined, plasează la sfârșit
              if (!a.Date_start) return 1;
              if (!b.Date_start) return -1;

              // Convertim șirurile de caractere în obiecte Date
              const dateA = new Date(a.Date_start);
              const dateB = new Date(b.Date_start);

              // Comparăm timestamp-urile pentru a sorta descrescător
              return dateB - dateA;
            })
            .map((item) => ({
              ...item,
              Date_start: this.formatDate(item.Date_start),
              Date_finish: this.formatDate(item.Date_finish),
            }));
        })
        .catch((error) => {
          console.error("Eroare la obținerea experienței profesionale:", error);
        });

      // Obține soft skills
      axios
        .get("/candidati/softskills", { params: { id_foreign: candidateId } })
        .then((response) => {
          this.softSkills = response.data;
        })
        .catch((error) => {
          console.error("Eroare la obținerea soft skills:", error);
        });

      // Obține link-urile
      axios
        .get("/candidati/links", { params: { id_foreign: candidateId } })
        .then((response) => {
          this.candidateLinks = response.data[0] || {}; // Presupunem că există un singur set de link-uri per candidat
        })
        .catch((error) => {
          console.error("Eroare la obținerea link-urilor:", error);
        });

      // Obține alte documente
      axios
        .get("/candidati/otherdocuments", {
          params: { id_foreign: candidateId },
        })
        .then((response) => {
          this.otherDocuments = response.data;
        })
        .catch((error) => {
          console.error("Eroare la obținerea altor documente:", error);
        });
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const datePart = String(dateString).split("T")[0];
      const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[3]}.${m[2]}.${m[1]}` : datePart;
    },
    // Metode pentru dialogul de editare a candidatului
    openEditDialog() {
      this.isEdit = true;
      this.editCandidate = { ...this.candidate };
      // Convertim data nașterii în formatul necesar pentru inputul de tip date
      if (this.editCandidate.Dateofbirth) {
        this.editCandidate.Dateofbirth = this.formatDateForInput(
          this.editCandidate.Dateofbirth
        );
      }
      this.editDialog = true;
    },
    formatDateForInput(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const day = ("0" + date.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    },
    onDialogSave(form) {
      this.updateCandidate(form);
    },
    updateCandidate(form) {
      const candidate = form || this.editCandidate;
      if (!candidate.id) {
        this.notify({
          message: "ID-ul candidatului nu este definit. Verificați datele.",
          color: "error",
        });
        return;
      }

      axios
        .put(`/candidati/candidats/${candidate.id}`, candidate)
        .then(() => {
          this.notify({ message: "Candidatul a fost actualizat cu succes", color: "success" });
          this.editDialog = false;
          this.fetchCandidateDetails(); // Reîncarcă detaliile candidatului
        })
        .catch((error) => {
          console.error("Eroare la actualizarea candidatului:", error);
          this.notify({ message: "A apărut o eroare la actualizarea candidatului.", color: "error" });
        });
    },
    notify({ message, color }) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    // ... alte metode existente
  },
});
</script>

<style scoped>
/* Aliniere la stânga (regula globală), suprascrie `#app { text-align: center }`. */
.cand-detail {
  text-align: left;
}

/* Header curat de workspace (stil de referință CRM). */
.cand-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 2px 0 10px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  margin-bottom: 16px;
}
.cand-detail__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 0;
}
.cand-detail__header-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-tertiary));
  opacity: 0.85;
}
.cand-detail__titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.cand-detail__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.25;
  color: var(--app-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cand-detail__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cand-detail__about {
  white-space: pre-line;
  color: rgba(var(--v-theme-on-surface), 0.72);
}

/* ── Panouri dense detaliu candidat (design.md L76-L78, L85) ── */
.cd-panels {
  align-items: stretch;
}
.cd-panel {
  padding: 16px 18px;
  height: 100%;
}
.cd-panel__head {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-secondary));
  padding-bottom: 8px;
  margin-bottom: 10px;
  border-bottom: 1px solid var(--ncrm-color-border-subtle, rgba(var(--v-theme-on-surface), 0.08));
}
.cd-panel__head-icon {
  opacity: 0.7;
  flex-shrink: 0;
}
.cd-props {
  display: flex;
  flex-direction: column;
}
.cd-prop {
  display: flex;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.05);
  font-size: 0.85rem;
  line-height: 1.35;
}
.cd-prop:last-child {
  border-bottom: none;
}
.cd-prop__label {
  color: rgba(var(--v-theme-on-surface), 0.55);
  flex: 0 0 38%;
}
.cd-prop__value {
  color: var(--app-text-strong);
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
}
.cd-jobs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cd-job {
  border: 1px solid var(--ncrm-color-border-subtle, rgba(var(--v-theme-on-surface), 0.06));
  border-radius: var(--app-radius-base);
  padding: 8px 10px;
}
.cd-job__title {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--app-text-strong);
}
.cd-job__meta {
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-top: 2px;
}
.cd-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}
.cd-link-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: var(--app-radius-pill);
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
  font-size: 0.8rem;
  text-decoration: none;
  transition: background 0.15s ease;
}
.cd-link-chip:hover {
  background: rgba(var(--v-theme-primary), 0.14);
}
.cd-applications {
  margin-top: 14px;
}
.cd-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.cd-empty {
  color: rgba(var(--v-theme-on-surface), 0.4);
  font-size: 0.85rem;
}

/* Asigură-te că v-card ocupă întreaga înălțime a coloanei */
.v-card {
  display: flex;
  flex-direction: column;
}

.v-card-text {
  flex-grow: 1;
}

/* Stilizare pentru butonul de editare */
.edit-button {
  position: absolute;
  top: 10px;
  right: 10px;
}

/* Stilizare pentru Snackbar */
.v-snackbar {
  max-width: 300px;
}

/* Stilizare pentru butonul de adăugare comentariu */
.mb-4 {
  margin-bottom: 16px;
}
</style>
